// useNotifications.js
// ─────────────────────────────────────────────────────────────────────────────
// Real-time notifications via Server-Sent Events (SSE).
// Falls back to 30-second polling if SSE is unavailable or repeatedly fails.
//
// Usage (unchanged API):
//   const { notifications, dismissOne, dismissAll, refresh } = useNotifications();
//
// SSE flow:
//   1. openSse() ruft getValidToken() — refresht den Token wenn nötig, BEVOR
//      die EventSource-Verbindung aufgebaut wird. Verhindert SSE-401-Kaskaden
//      nach Standby/längerer Inaktivität.
//   2. Server sends 'init' event (full state) immediately on connect
//   3. Server sends 'notifications' events (delta, new IDs only)
//   4. Server sends 'reconnect' after ~10 min → EventSource auto-reconnects
//      mit dem neuesten Token aus localStorage (silently refreshed by AuthContext)
//   5. On 3 consecutive EventSource errors → silently falls back to polling
//   6. On tab visibility change → reconnects SSE for fresh delta
//   7. On hejvi:token-refreshed → immer SSE reconnecten (auch wenn im Polling-Fallback)
//   8. On hejvi:logged-out → SSE und Polling sofort stoppen

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

// ── Config ────────────────────────────────────────────────────────────────────

/** Max consecutive SSE errors before falling back to polling */
const SSE_MAX_ERRORS = 3;

/** Fallback polling interval (ms) — only used when SSE is unavailable */
const POLL_FALLBACK_MS = 30_000;

// ── Hook ──────────────────────────────────────────────────────────────────────

export default function useNotifications() {
  const { makeAuthenticatedRequest, getValidToken, logout } = useAuth();

  const [notifications, setNotifications] = useState([]);

  // SSE state refs — stable across renders, no re-render on change
  const sseRef          = useRef(null);   // current EventSource instance
  const errorCount      = useRef(0);      // consecutive SSE errors
  const usingSSE        = useRef(false);  // true while SSE is active
  const pollTimerRef    = useRef(null);   // fallback polling timer
  const reconnectTimer  = useRef(null);   // backoff timer für SSE-Reconnect
  const reconnectDelay  = useRef(1000);   // aktueller Backoff in ms (1s→2s→4s→8s→max 30s)

  // ── Helpers ──────────────────────────────────────────────────────────────

  /** Merge incoming notifications into state, newest first, deduped, max 20 */
  const mergeNotifications = useCallback((incoming) => {
    if (!Array.isArray(incoming) || incoming.length === 0) return;
    setNotifications((prev) => {
      const existingIds = new Set(prev.map((n) => n.id));
      const fresh = incoming.filter((n) => !existingIds.has(n.id));
      if (fresh.length === 0) return prev;
      return [...fresh, ...prev].slice(0, 20);
    });
  }, []);

  /** Full replace (used on 'init' event) */
  const replaceNotifications = useCallback((data) => {
    if (Array.isArray(data)) setNotifications(data);
  }, []);

  // ── Fallback: HTTP fetch ──────────────────────────────────────────────────

  // stopPolling und closeSse müssen VOR fetchNotifications stehen,
  // damit fetchNotifications sie in den Deps referenzieren kann.

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // ── SSE connection ────────────────────────────────────────────────────────

  const closeSse = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }
    usingSSE.current = false;
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await makeAuthenticatedRequest('/notifications');
      if (data?.status === 'ok' && Array.isArray(data.data)) {
        replaceNotifications(data.data);
      }
    } catch (err) {
      // 401 = Refresh-Token abgelaufen, makeAuthenticatedRequest hat bereits logout() aufgerufen.
      // Polling sofort stoppen — hejvi:logged-out wird darüber hinaus SSE schließen.
      if (err?.status === 401) {
        stopPolling();
        closeSse();
        return;
      }
      console.debug('[useNotifications] poll fetch error', err);
    }
  }, [makeAuthenticatedRequest, replaceNotifications, stopPolling, closeSse]);

  const startPolling = useCallback(() => {
    if (pollTimerRef.current) return;
    fetchNotifications();
    pollTimerRef.current = setInterval(fetchNotifications, POLL_FALLBACK_MS);
  }, [fetchNotifications]);

  const openSse = useCallback(async () => {
    if (typeof EventSource === 'undefined') {
      console.info('[useNotifications] EventSource not available — using polling');
      startPolling();
      return;
    }

    // ── FIX: getValidToken() statt rohem localStorage-Read ──────────────────
    // getValidToken() triggert einen Silent-Refresh wenn der Token abläuft oder
    // bereits abgelaufen ist. Verhindert, dass nach Standby eine EventSource mit
    // einem bereits invaliden Token aufgebaut wird → keine SSE-401-Kaskade mehr.
    const token = await getValidToken();

    if (!token) {
      // getValidToken() gibt null zurück wenn:
      //   a) Kein Refresh-Token in localStorage → echte Session-Abgelaufen → logout
      //   b) Refresh-Token vorhanden, aber Netz nach Standby noch nicht bereit → NICHT logout
      //      Der 'online'-Handler reconnectet SSE sobald das Netz zurückkommt.
      if (!navigator.onLine) {
        console.debug('[useNotifications] no valid token — offline, waiting for network');
        return; // 'online'-Handler übernimmt
      }
      console.debug('[useNotifications] no valid token after refresh attempt — logging out');
      logout();
      return;
    }

    closeSse();

    // Resolve the API base URL
    let base = '';
    try {
      const { appConfig } = await import('../config');
      base = appConfig?.apiBaseUrl ?? '';
    } catch { /* use empty base */ }

    const url = `${base}/sse/notifications?token=${encodeURIComponent(token)}`;
    const sse = new EventSource(url);
    sseRef.current   = sse;
    usingSSE.current = true;
    stopPolling(); // Polling stoppen sobald SSE aktiv ist

    // ── 'init' — full current notification list ───────────────────────────
    sse.addEventListener('init', (e) => {
      errorCount.current     = 0;
      reconnectDelay.current = 1000; // Backoff nach erfolgreicher Verbindung zurücksetzen
      try {
        const payload = JSON.parse(e.data);
        if (payload?.status === 'ok') replaceNotifications(payload.data);
      } catch { /* ignore malformed */ }
    });

    // ── 'notifications' — delta push (new IDs only) ───────────────────────
    sse.addEventListener('notifications', (e) => {
      errorCount.current = 0;
      try {
        const payload = JSON.parse(e.data);
        if (payload?.status === 'ok') mergeNotifications(payload.data);
      } catch { /* ignore malformed */ }
    });

    // ── 'reconnect' — server gracefully closing after max lifetime ────────
    sse.addEventListener('reconnect', () => {
      closeSse();
      setTimeout(openSse, 500);
    });

    // ── error handler ─────────────────────────────────────────────────────
    // openSse() liest jetzt über getValidToken() einen aktuellen Token —
    // daher löst ein abgelaufenes Token beim Reconnect einen Silent-Refresh
    // aus, bevor die neue EventSource angelegt wird.
    // Exponentieller Backoff (1s→2s→4s→8s→30s max) verhindert Tight-Loops.
    // Nach SSE_MAX_ERRORS aufeinanderfolgenden Fehlern: Polling-Fallback.
    sse.onerror = () => {
      errorCount.current += 1;
      console.debug(`[useNotifications] SSE error #${errorCount.current}, backoff=${reconnectDelay.current}ms`);

      closeSse(); // alten EventSource sofort wegwerfen

      if (errorCount.current >= SSE_MAX_ERRORS) {
        console.warn('[useNotifications] SSE repeatedly failed — falling back to polling');
        // errorCount NICHT zurücksetzen — erst hejvi:token-refreshed darf das.
        // Verhindert, dass direkt nach dem Polling-Start ein neuer SSE-Versuch
        // den Backoff-Schutz umgeht.
        startPolling();
        return;
      }

      // Mit aktuellem Token (ggf. gerade von AuthContext refreshed) neu verbinden.
      // getValidToken() in openSse() sorgt dafür, dass vor dem Reconnect ein
      // frisches Token geholt wird.
      const delay = reconnectDelay.current;
      reconnectDelay.current = Math.min(reconnectDelay.current * 2, 30_000);
      reconnectTimer.current = setTimeout(() => {
        reconnectTimer.current = null;
        openSse();
      }, delay);
    };
  }, [closeSse, getValidToken, logout, mergeNotifications, replaceNotifications, startPolling, stopPolling]);

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  useEffect(() => {
    openSse();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (usingSSE.current || sseRef.current) {
          // SSE war aktiv oder hängt noch → neu aufbauen (getValidToken refresht wenn nötig)
          closeSse();
          openSse();
        } else if (!pollTimerRef.current) {
          // Kein SSE, kein Polling → Polling starten
          startPolling();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // ── FIX: online-Event — Netz nach Standby/Disconnect zurück ───────────
    // Wenn getValidToken() beim Wake-Versuch wegen fehlendem Netz null zurückgab,
    // hat openSse() nichts geöffnet (statt fälschlicherweise logout zu rufen).
    // Sobald der Browser 'online' meldet, Backoff + Fehlerzähler resetten
    // und SSE neu aufbauen — getValidToken() kann jetzt den Refresh durchführen.
    const handleOnline = () => {
      console.debug('[useNotifications] network online — reconnecting SSE');
      errorCount.current     = 0;
      reconnectDelay.current = 1000;
      closeSse();
      stopPolling();
      openSse();
    };
    window.addEventListener('online', handleOnline);

    // ── FIX: hejvi:token-refreshed — IMMER SSE reconnecten ────────────────
    // Vorher: nur wenn usingSSE.current || sseRef.current — d.h. wenn wir bereits
    // im Polling-Fallback waren, passierte gar nichts und der frische Token
    // wurde für SSE nie genutzt.
    // Neu: immer errorCount zurücksetzen und SSE neu aufbauen, egal ob vorher
    // SSE oder Polling aktiv war.
    const handleTokenRefreshed = () => {
      console.debug('[useNotifications] token refreshed — reconnecting SSE');
      errorCount.current     = 0;   // SSE-Fehler-Zähler zurücksetzen → volle Versuche wieder
      reconnectDelay.current = 1000; // Backoff zurücksetzen
      closeSse();
      stopPolling();
      openSse();
    };
    window.addEventListener('hejvi:token-refreshed', handleTokenRefreshed);

    // ── hejvi:logged-out — SSE und Polling sofort stoppen ─────────────────
    const handleLoggedOut = () => {
      console.debug('[useNotifications] logged out — stopping all connections');
      closeSse();
      stopPolling();
    };
    window.addEventListener('hejvi:logged-out', handleLoggedOut);

    // ── hejvi:language-changed — preferred_language in DB synchronisieren ──
    // LanguageContext dispatcht dieses Event bei jedem Sprachwechsel.
    // useNotifications hat Zugriff auf makeAuthenticatedRequest → übernimmt DB-Update.
    const handleLanguageChanged = async ({ detail }) => {
      const { id } = detail || {};
      if (!id) return;
      try {
        await makeAuthenticatedRequest('/user/language', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ preferred_language: id }),
        });
        console.debug(`[useNotifications] preferred_language in DB → ${id}`);
        // Notifications neu laden — localise() nutzt jetzt den neuen DB-Wert
        fetchNotifications();
      } catch (err) {
        console.debug('[useNotifications] language DB update failed (non-critical)', err);
      }
    };
    window.addEventListener('hejvi:language-changed', handleLanguageChanged);

    return () => {
      closeSse();
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('hejvi:token-refreshed', handleTokenRefreshed);
      window.removeEventListener('hejvi:logged-out', handleLoggedOut);
      window.removeEventListener('hejvi:language-changed', handleLanguageChanged);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Dismiss one ───────────────────────────────────────────────────────────

  const dismissOne = useCallback(
    async (notificationId) => {
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      try {
        await makeAuthenticatedRequest('/notifications/dismiss', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ notification_id: notificationId }),
        });
      } catch (err) {
        console.debug('[useNotifications] dismiss error', err);
        fetchNotifications();
      }
    },
    [makeAuthenticatedRequest, fetchNotifications]
  );

  // ── Dismiss all ───────────────────────────────────────────────────────────

  const dismissAll = useCallback(
    async (ids) => {
      const idsToSend = ids ?? notifications.map((n) => n.id);
      setNotifications([]);
      try {
        await makeAuthenticatedRequest('/notifications/dismiss-all', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ notification_ids: idsToSend }),
        });
      } catch (err) {
        console.debug('[useNotifications] dismiss-all error', err);
        fetchNotifications();
      }
    },
    [makeAuthenticatedRequest, notifications, fetchNotifications]
  );

  const refresh = useCallback(() => fetchNotifications(), [fetchNotifications]);

  return { notifications, dismissOne, dismissAll, refresh };
}
